import React, { useContext, useEffect, useMemo, useState } from "react";
import debounce from "lodash.debounce";
import useSWR from "swr";

import {
  emitSearchAction,
  emitSearchboxImpression,
  useExperiment,
} from "@catchoftheday/analytics";
import {
  Box,
  BoxProps,
  Highlighter,
  InputDropdownListItem,
  InputDropdownOnInputChange,
  InputDropdownRenderListItem,
  SearchBar as SearchBarComponent,
  SearchBarProps,
  Span,
} from "@catchoftheday/cg-components";
import { css, styled, themeGet } from "@catchoftheday/theme";
import { encodeSearchTerm } from "@catchoftheday/utilities";

import { fetcher } from "../../api/index";
import { searchRedirectUrl } from "../../api/searchRedirectUrl";
import { HeaderContext } from "../../context";
import {
  AxiosResponse,
  SearchAutoComplete,
  SearchAutoCompleteCategory,
} from "../../types/index";
import { getQueryStringValue } from "../../utilities/index";

const CATEGORY_FILTER_EXPERIMENT = "search_category_filters_apr23";

type SearchItemContainerProps = BoxProps & {
  isHighlighted: boolean;
};

const SearchItemContainer = styled(Box)<SearchItemContainerProps>`
  ${(props) =>
    props.isHighlighted
      ? css`
          background-color: ${themeGet("colors.ui.dropDownBg")(props)};
          color: ${themeGet("colors.ui.dropDownText")(props)};
        `
      : `background-color: white; color: #333;`}

  &:hover {
    color: ${themeGet("colors.ui.dropDownText")};
    background-color: ${themeGet("colors.ui.dropDownBg")};
  }
`;

function transformSearchData(
  searchData: SearchAutoComplete[]
): InputDropdownListItem<SearchAutoCompleteCategory>[] {
  const result = searchData.reduce((acc, curr) => {
    const { value, categories } = curr;
    const filteredCategories = categories
      ? categories.filter((cat) => cat)
      : [];

    const searchTermOnlySuggestion = [
      {
        title: value,
      },
    ];

    const suggestions =
      filteredCategories.length > 0
        ? searchTermOnlySuggestion.concat(
            filteredCategories.map((cat) => ({
              title: value,
              value: cat,
            }))
          )
        : searchTermOnlySuggestion;

    return acc.concat(suggestions);
  }, [] as InputDropdownListItem<SearchAutoCompleteCategory>[]);

  return result;
}

export const SearchBar = (
  props: Omit<SearchBarProps<SearchAutoCompleteCategory>, "data">
) => {
  const headerContext = useContext(HeaderContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputTerm, setInputTerm] = useState("");
  const [showSearchData, setShowSearchData] = useState(false);
  const {
    variants: [categoryExperiment],
  } = useExperiment([CATEGORY_FILTER_EXPERIMENT]);

  const debouncedSetSearchTerm = useMemo(
    () => debounce(setSearchTerm, 150),
    [setSearchTerm]
  );

  const { data, isValidating } = useSWR<AxiosResponse<SearchAutoComplete[]>>(
    () =>
      searchTerm
        ? `/autocomplete/2?query=${encodeSearchTerm(searchTerm)}`
        : null,
    fetcher,
    {
      dedupingInterval: 0,
      revalidateOnFocus: false,
    }
  );

  const renderSearchItem: InputDropdownRenderListItem<
    SearchAutoCompleteCategory
  > = ({ itemData, isHighlighted }) => {
    return (
      <SearchItemContainer
        isHighlighted={isHighlighted}
        fontWeight="light"
        fontSize="sm"
        px="26px"
        py="5px"
      >
        <Highlighter highlight={inputTerm} text={itemData.title} reverse />
        <Span color="secondary" fontSize="xs">
          {itemData.value ? ` in ${itemData.value?.name}` : ""}
        </Span>
      </SearchItemContainer>
    );
  };

  const onInputChange: InputDropdownOnInputChange = (value, reason) => {
    if (reason.onChange) {
      debouncedSetSearchTerm.cancel();
      // Only search if input term is different from current search term
      debouncedSetSearchTerm(value);

      // Update input term state to match with user input
      setInputTerm(value);

      // CLear suggestions if input term is different from current search term
      if (value.trim() != searchTerm.trim()) {
        setShowSearchData(false);
      }
    }
  };

  const searchData = data?.data || [];
  const suggestions =
    searchData && searchData.length > 0 ? transformSearchData(searchData) : [];
  const categoryFacet = categoryExperiment?.bucket
    ? "category_correlated"
    : "category";

  async function navigateToSearchPage(
    term: string,
    category?: SearchAutoCompleteCategory,
    itemData?: InputDropdownListItem<SearchAutoCompleteCategory>
  ) {
    if (!term || term.length == 1) return;

    const encodedSearchTerm = encodeSearchTerm(term);

    const query = `/search?query=${encodedSearchTerm}`;
    const params = `&search_src=topbar&ac=0${
      category ? `&f[${categoryFacet}]=${category.id}&acs=1` : ""
    }`;
    const url = `${query}${encodeURI(params)}`;

    const redirectUrl = await searchRedirectUrl(term);

    const isSpecialSearch = !!(redirectUrl || category);

    const specialSearchDetails = isSpecialSearch
      ? {
          type: redirectUrl ? "redirect" : "search_in_category",
          category: category?.name,
          redirect_path: redirectUrl || undefined,
        }
      : undefined;

    emitSearchAction({
      user_text: inputTerm,
      is_typehead: !!(itemData && itemData.title !== inputTerm),
      final_text: term,
      is_special_search: isSpecialSearch,
      special_search_details: specialSearchDetails,
    });

    if (redirectUrl) {
      return location.assign(redirectUrl);
    }

    window.location.assign(url);
  }

  const urlSearchQuery = getQueryStringValue("query");

  useEffect(() => {
    if (urlSearchQuery) {
      setInputTerm(urlSearchQuery);
      setSearchTerm(urlSearchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchTerm) {
      setShowSearchData(true);
    }
  }, [searchTerm]);

  return (
    <SearchBarComponent
      isLoading={!!searchTerm && isValidating}
      onInputChange={onInputChange}
      defaultValue={urlSearchQuery}
      onItemSelected={(itemData) => {
        const term = itemData?.title || inputTerm;
        const category = itemData?.value;
        const categoryKey = `f[${categoryFacet}]`;

        if (headerContext.onSearchChange) {
          headerContext.onSearchChange({
            query: term,
            [categoryKey]: category ? category.id : undefined,
            acs: "1",
          });
        } else {
          navigateToSearchPage(term, category, itemData);
        }
      }}
      onSearchButtonClicked={() => {
        if (headerContext.onSearchChange) {
          headerContext.onSearchChange({
            query: inputTerm,
          });
        } else {
          navigateToSearchPage(inputTerm, null);
        }
      }}
      onInputFocus={emitSearchboxImpression}
      data={showSearchData ? suggestions : []}
      renderListItem={renderSearchItem}
      {...props}
    />
  );
};

export default SearchBar;
